import { ChatIcon } from '@chakra-ui/icons';
import { Box, HStack, Textarea, VStack } from '@chakra-ui/react';
import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import GradientPopover from 'components/GradientPopover';
import Icon from 'components/Icon';
import Text from 'components/Text';
import { useOnboardingMissions } from 'features/onboarding-missions/useOnboardingMissions';
import { EdgeFunctionName, SubmitFeedbackParams } from 'types/edgeFunctions';
import { ERoute } from 'types/frontend';
import { isLocalEnvironment } from 'utils/env';

import { useMediaQueries } from '../MediaQueriesProvider';

const MAIN_PAGES: string[] = [
  ERoute.Dashboard,
  ERoute.Projects,
  ERoute.Challenges,
  ERoute.PracticeProblems,
  ERoute.Prototypes,
  ERoute.CodeFrames,
];

const StarRating = ({
  rating,
  hovered,
  onRate,
  onHover,
  onLeave,
}: {
  rating: number;
  hovered: number;
  onRate: (n: number) => void;
  onHover: (n: number) => void;
  onLeave: () => void;
}) => {
  return (
    <HStack spacing={1} onMouseLeave={onLeave}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Box
          key={n}
          as="button"
          type="button"
          onClick={() => onRate(n)}
          onMouseEnter={() => onHover(n)}
          cursor="pointer"
          fontSize="24px"
          transition="transform 0.1s ease-out"
          _hover={{ transform: 'scale(1.15)' }}
          lineHeight="1"
        >
          {n <= (hovered || rating) ? '★' : '☆'}
        </Box>
      ))}
    </HStack>
  );
};

const FeedbackButton = () => {
  const { pathname } = useLocation();
  const { id: userId } = useCurrentUser();
  const { hasAnyCompleted } = useOnboardingMissions();
  const { isSmallScreen } = useMediaQueries();
  const supabase = useSupabaseTypedClient();
  const { toastError } = useToastWrapper();
  const popoverRef = useRef<{ onClose: () => void }>(null);

  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isMainPage = MAIN_PAGES.includes(pathname);
  const isDev = isLocalEnvironment();

  if (!isMainPage || !userId) {
    return null;
  }

  if (!hasAnyCompleted && !isDev) {
    return null;
  }

  const handleSubmit = async () => {
    if (rating === 0) return;

    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke(EdgeFunctionName.SubmitFeedback, {
        body: {
          rating,
          message: message.trim() || undefined,
          page: pathname,
        } as SubmitFeedbackParams,
      });

      if (error) throw error;

      setIsSubmitted(true);
    } catch {
      toastError('Failed to submit feedback', true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccess = () => {
    popoverRef.current?.onClose();
  };

  const handleReset = () => {
    setRating(0);
    setHovered(0);
    setMessage('');
    setIsSubmitted(false);
  };

  return (
    <GradientPopover
      ref={popoverRef}
      tooltip="Share feedback"
      triggerComponent={
        <Box
          as="button"
          display="flex"
          alignItems="center"
          gap="6px"
          px={2.5}
          py={1}
          borderRadius={8}
          border="1px solid"
          borderColor="whiteAlpha.300"
          bg="transparent"
          cursor="pointer"
          transition="all 0.15s ease-out"
          _hover={{ borderColor: 'whiteAlpha.500', bg: 'whiteAlpha.100' }}
          aria-label="Share feedback"
          height="32px"
        >
          <ChatIcon boxSize={3.5} />
          {!isSmallScreen && (
            <Text fontSize="small" fontWeight="regular" whiteSpace="nowrap">
              Feedback
            </Text>
          )}
        </Box>
      }
      header={isSubmitted ? null : 'Share Feedback'}
      width="320px"
      onPopoverStateChange={(isOpen) => {
        if (!isOpen) setTimeout(handleReset, 200);
      }}
    >
      {isSubmitted ? (
        <VStack spacing={3} py={2} alignItems="center" width="100%">
          <Icon type="CheckColor" size={32} />
          <Text fontSize="medium" fontWeight="semibold">
            Thanks for your feedback!
          </Text>
          <Text fontSize="small" opacity="secondary" textAlign="center">
            Your input helps us improve BigDevSoon.
          </Text>
          <Box
            as="button"
            px={4}
            py={1.5}
            borderRadius={6}
            bg="whiteAlpha.200"
            _hover={{ bg: 'whiteAlpha.300' }}
            transition="all 0.15s ease-out"
            onClick={handleCloseSuccess}
          >
            <Text fontSize="small">Close</Text>
          </Box>
        </VStack>
      ) : (
        <VStack spacing={4} alignItems="flex-start" width="100%">
          <VStack spacing={1} alignItems="flex-start">
            <Text fontSize="small" opacity="secondary">
              How&apos;s your experience?
            </Text>
            <StarRating
              rating={rating}
              hovered={hovered}
              onRate={setRating}
              onHover={setHovered}
              onLeave={() => setHovered(0)}
            />
          </VStack>
          <Textarea
            placeholder="Tell us what you think... (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
            size="sm"
            borderColor="whiteAlpha.300"
            _hover={{ borderColor: 'whiteAlpha.400' }}
            _focus={{ borderColor: 'purple.400', boxShadow: 'none' }}
            resize="none"
            rows={3}
          />
          <HStack width="100%" justifyContent="space-between" alignItems="center">
            <Text fontSize="xMicro" opacity="tertiary">
              {message.length}/2000
            </Text>
            <Button
              scheme="primary"
              size="sm"
              onClick={handleSubmit}
              isDisabled={rating === 0}
              isLoading={isSubmitting}
            >
              Send
            </Button>
          </HStack>
        </VStack>
      )}
    </GradientPopover>
  );
};

export default FeedbackButton;
